import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Container,
  Table,
  Button,
  Form,
  Row,
  Col,
  Card,
  Alert,
} from "react-bootstrap";
import NavigationBar from "../components/NavigationBar";
import Footer from "../components/Footer";
import TrackerCard from "../components/TrackerCard";
import MonthCard from "../components/MonthCard";
import PriceChartModal from "../components/PriceChartModal";
import FlightSelectionModal from "../components/FlightSelectionModal";
import ConfirmDialog from "../components/ConfirmDialog";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "react-toastify";
import { useI18n } from "../i18n";
import { Tracker } from "./Dashboard";

interface UserRow {
  id: number;
  username: string;
  email: string;
  is_admin: boolean;
  is_disabled: boolean;
  must_change_password: boolean;
}

interface ResetRequest {
  id: number;
  username: string;
  created_at: string;
}

const Admin: React.FC = () => {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [requests, setRequests] = useState<ResetRequest[]>([]);
  const [newUsername, setNewUsername] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [userTrackers, setUserTrackers] = useState<Tracker[]>([]);
  const [loadingTrackers, setLoadingTrackers] = useState(false);
  const [showPriceChart, setShowPriceChart] = useState(false);
  const [selectedTracker, setSelectedTracker] = useState<Tracker | null>(null);
  const [showFlightSelection, setShowFlightSelection] = useState(false);
  const [editingFlightType, setEditingFlightType] = useState<"outbound" | "return">("outbound");
  
  // Confirmation dialogs state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [userToResetPassword, setUserToResetPassword] = useState<number | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      const res = await axios.get("/api/admin/users");
      setUsers(res.data);
    } catch (err) {
      toast.error(t("messages.userCreateError"));
    }
  }, [t]);

  const loadRequests = useCallback(async () => {
    try {
      const res = await axios.get("/api/admin/password-reset-requests");
      setRequests(res.data);
    } catch (err) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!user?.is_admin) return;
    loadUsers();
    loadRequests();
  }, [user, loadUsers, loadRequests]);

  const createUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post("/api/admin/users", {
        username: newUsername,
        email: newEmail,
        is_admin: newIsAdmin,
      });
      toast.success(t("messages.userCreated"));
      setNewUsername("");
      setNewEmail("");
      setNewIsAdmin(false);
      await loadUsers();
    } catch (err) {
      toast.error(t("messages.userCreateError"));
    }
  };

  const disableUser = async (id: number) => {
    try {
      await axios.put(`/api/admin/users/${id}/disable`);
      toast.success(t("messages.userDisabled"));
      await loadUsers();
    } catch (err) {
      toast.error(t("messages.userCreateError"));
    }
  };

  const enableUser = async (id: number) => {
    try {
      await axios.put(`/api/admin/users/${id}/enable`);
      toast.success(t("messages.userEnabled"));
      await loadUsers();
    } catch (err) {
      toast.error(t("messages.userCreateError"));
    }
  };

  const deleteUser = async (id: number) => {
    setUserToDelete(id);
    setShowDeleteConfirm(true);
  };
  
  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(`/api/admin/users/${userToDelete}`);
      toast.success(t("messages.userDeleted"));
      await loadUsers();
    } catch (err) {
      toast.error(t("messages.userCreateError"));
    } finally {
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const resetPassword = async (id: number) => {
    setUserToResetPassword(id);
    setShowResetPasswordConfirm(true);
  };
  
  const confirmResetPassword = async () => {
    if (!userToResetPassword) return;
    try {
      const res = await axios.post(`/api/admin/users/${userToResetPassword}/reset-password`);
      const newPass = res.data.new_password;
      toast.success(t("messages.passwordResetSuccess").replace("{{password}}", newPass));
      await loadUsers();
    } catch (err) {
      toast.error(t("messages.userCreateError"));
    } finally {
      setShowResetPasswordConfirm(false);
      setUserToResetPassword(null);
    }
  };

  const clearRequest = async (id: number) => {
    try {
      await axios.delete(`/api/admin/password-reset-requests/${id}`);
      await loadRequests();
    } catch (err) {}
  };

  const viewUserTrackers = async (selectedUser: UserRow): Promise<Tracker[]> => {
    setSelectedUser(selectedUser);
    setLoadingTrackers(true);
    try {
      const response = await axios.get(`/api/admin/users/${selectedUser.id}/trackers`);
      setUserTrackers(response.data);
      return response.data;
    } catch (err) {
      toast.error(t("admin.loadTrackersError"));
      return [];
    } finally {
      setLoadingTrackers(false);
    }
  };

  const handleDeleteTracker = async (trackerId: number) => {
    if (!window.confirm(t("common.confirmDeleteMessage"))) return;
    if (!selectedUser) return;
    
    try {
      await axios.delete(`/api/admin/users/${selectedUser.id}/trackers/${trackerId}`);
      toast.success(t("messages.trackerDeleted"));
      await viewUserTrackers(selectedUser);
    } catch (err) {
      toast.error(t("messages.trackerDeleteError"));
    }
  };

  const handleShowChart = (tracker: Tracker) => {
    // Use current tracker data (already loaded) to avoid triggering re-render that collapses month panels
    setSelectedTracker(tracker);
    setShowPriceChart(true);
  };

  const handleEditFlight = (
    trackerId: number,
    flightType: "outbound" | "return"
  ) => {
    const tracker = userTrackers.find((t) => t.id === trackerId);
    if (tracker) {
      setSelectedTracker(tracker);
      setEditingFlightType(flightType);
      setShowFlightSelection(true);
    }
  };

  const handleFlightSelected = async () => {
    if (selectedUser) {
      await viewUserTrackers(selectedUser);
    }
  };

  const handleThresholdUpdated = async () => {
    if (selectedUser) {
      await viewUserTrackers(selectedUser);
    }
  };

  const handleUpdateThreshold = async (trackerId: number, threshold: number | null) => {
    if (!selectedUser) return;
    
    await axios.put(`/api/admin/users/${selectedUser.id}/trackers/${trackerId}/threshold`, {
      threshold_price: threshold,
    });
  };

  const handleGetAvailableFlights = async (trackerId: number, flightType: string) => {
    if (!selectedUser) return [];
    
    try {
      const response = await axios.get(
        `/api/admin/users/${selectedUser.id}/trackers/${trackerId}/available-flights`,
        { params: { flight_type: flightType } }
      );
      return response.data;
    } catch (err) {
      console.error("Error fetching available flights:", err);
      throw err;
    }
  };

  const handleSelectFlight = async (trackerId: number, payload: any) => {
    if (!selectedUser) return;
    
    try {
      await axios.put(
        `/api/admin/users/${selectedUser.id}/trackers/${trackerId}/select-flight`,
        payload
      );
    } catch (err) {
      console.error("Error selecting flight:", err);
      throw err;
    }
  };

  const backToUserList = () => {
    setSelectedUser(null);
    setUserTrackers([]);
  };

  // Helper function to get month/year from date
  const getMonthYear = (dateString: string): string => {
    const date = new Date(dateString);
    const monthNames = lang === 'it' 
      ? ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
         'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre']
      : ['January', 'February', 'March', 'April', 'May', 'June',
         'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Helper function to get sort key for month/year
  const getMonthYearSortKey = (dateString: string): string => {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  // Group trackers by month/year
  const groupedTrackers = useMemo(() => {
    const groups: { [key: string]: { sortKey: string; trackers: Tracker[] } } = {};
    
    userTrackers.forEach((tracker) => {
      const monthYear = getMonthYear(tracker.departure_date);
      const sortKey = getMonthYearSortKey(tracker.departure_date);
      
      if (!groups[monthYear]) {
        groups[monthYear] = { sortKey, trackers: [] };
      }
      groups[monthYear].trackers.push(tracker);
    });
    
    // Sort groups by date (earliest first) and include sortKey
    return Object.entries(groups)
      .sort(([, a], [, b]) => a.sortKey.localeCompare(b.sortKey))
      .map(([monthYear, data]) => ({
        monthYear,
        sortKey: data.sortKey,
        trackers: data.trackers,
      }));
  }, [userTrackers, lang]);

  if (!user?.is_admin) {
    return (
      <>
        <NavigationBar />
        <Container>
          <Card className="mt-4">
            <Card.Body>
              <h4>{t("admin.accessDeniedTitle")}</h4>
              <p>{t("admin.accessDeniedMessage")}</p>
            </Card.Body>
          </Card>
        </Container>
      </>
    );
  }

  // Show user's trackers view when a user is selected
  if (selectedUser) {
    return (
      <>
        <NavigationBar />
        <Container>
          <div className="mt-4 mb-3">
            <Button variant="outline-secondary" onClick={backToUserList}>
              <i className="bi bi-arrow-left"></i> {t("admin.backToUsers")}
            </Button>
          </div>
          
          <Alert variant="info">
            <i className="bi bi-person-circle"></i>{" "}
            {t("admin.viewingTrackersFor")}: <strong>{selectedUser.username}</strong> ({selectedUser.email})
          </Alert>

          <h3>{t("admin.userTrackers")}</h3>
          
          {loadingTrackers ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t("tracker.loading")}</span>
              </div>
            </div>
          ) : userTrackers.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="bi bi-inbox"
                style={{ fontSize: "4rem", color: "#ccc" }}
              ></i>
              <p className="text-muted mt-3">{t("tracker.noTrackers")}</p>
            </div>
          ) : (
            <>
              {groupedTrackers.map(({ monthYear, trackers: monthTrackers }) => (
                <MonthCard
                  key={monthYear}
                  monthYear={monthYear}
                  trackers={monthTrackers}
                  notificationCount={0}
                  onDelete={handleDeleteTracker}
                  onShowChart={handleShowChart}
                  onEditFlight={handleEditFlight}
                  onThresholdUpdated={handleThresholdUpdated}
                />
              ))}
            </>
          )}
        </Container>

        {selectedTracker && (
          <PriceChartModal
            show={showPriceChart}
            onHide={() => setShowPriceChart(false)}
            tracker={selectedTracker}
          />
        )}

        {selectedTracker && selectedTracker.flight_details && (
          <FlightSelectionModal
            show={showFlightSelection}
            onHide={() => setShowFlightSelection(false)}
            trackerId={selectedTracker.id}
            flightType={editingFlightType}
            currentFlight={
              editingFlightType === "outbound"
                ? selectedTracker.flight_details.outbound
                : selectedTracker.flight_details.return || undefined
            }
            onFlightSelected={handleFlightSelected}
            trackerDepartureDate={selectedTracker.departure_date}
            trackerReturnDate={selectedTracker.return_date || undefined}
            customGetAvailableFlights={handleGetAvailableFlights}
            customSelectFlight={handleSelectFlight}
          />
        )}
      </>
    );
  }

  return (
    <>
      <NavigationBar />
      <Container>
        <h2 className="mt-4">{t("admin.title")}</h2>

        <Row className="mt-3">
          <Col md={6}>
            <Card>
              <Card.Body>
                <h5>{t("admin.createUser")}</h5>
                <Form onSubmit={createUser}>
                  <Form.Group className="mb-2">
                    <Form.Label>{t("common.username") || "Username"}</Form.Label>
                    <Form.Control
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>{t("common.email") || "Email"}</Form.Label>
                    <Form.Control
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Check
                      type="checkbox"
                      label={t("admin.isAdmin") || "Is Admin"}
                      checked={newIsAdmin}
                      onChange={(e) => setNewIsAdmin(e.target.checked)}
                    />
                  </Form.Group>
                  <Button type="submit">{t("admin.createButton")}</Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card>
              <Card.Body>
                <h5>{t("admin.requests")}</h5>
                {requests.length === 0 ? (
                  <p>{t("admin.noRequests")}</p>
                ) : (
                  <Table size="sm">
                    <thead>
                      <tr>
                            <th>{t("common.username") || "Username"}</th>
                        <th>{t("common.date") || "Date"}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((r) => (
                        <tr key={r.id}>
                            <td>{r.username}</td>
                          <td>{new Date(r.created_at).toLocaleString()}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => clearRequest(r.id)}
                            >
                              {t("admin.delete")}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

          <h4 className="mt-4">{t("admin.registeredUsers")}</h4>
        
        {/* Mobile Card View */}
        <div className="admin-user-card">
          {users.map((u) => (
            <Card key={u.id} className="mb-3">
              <Card.Body>
                <div className="admin-user-card-header">
                  <div>
                    <h5 className="mb-0">{u.username}</h5>
                    <small className="text-muted">ID: {u.id}</small>
                  </div>
                  {u.is_admin && (
                    <span className="badge bg-primary">Admin</span>
                  )}
                </div>
                
                <div className="admin-user-info">
                  <div className="mb-1">
                    <i className="bi bi-envelope me-2"></i>
                    <small>{u.email}</small>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    {u.is_disabled && (
                      <span className="badge bg-warning text-dark">Disabled</span>
                    )}
                    {u.must_change_password && (
                      <span className="badge bg-info">Must Change Password</span>
                    )}
                  </div>
                </div>
                
                <div className="admin-user-actions mt-3">
                  {u.is_disabled ? (
                    <Button
                      size="sm"
                      onClick={() => enableUser(u.id)}
                    >
                      <i className="bi bi-check-circle me-1"></i>
                      {t("admin.enable")}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="warning"
                      onClick={() => disableUser(u.id)}
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      {t("admin.disable")}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="info"
                    onClick={() => viewUserTrackers(u)}
                  >
                    <i className="bi bi-eye me-1"></i>
                    {t("admin.viewTrackers")}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => deleteUser(u.id)}
                  >
                    <i className="bi bi-trash me-1"></i>
                    {t("admin.delete")}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => resetPassword(u.id)}
                  >
                    <i className="bi bi-key me-1"></i>
                    {t("admin.resetPassword")}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
        
        {/* Desktop Table View */}
        <Table striped bordered hover className="admin-table-responsive">
          <thead>
            <tr>
              <th>{t("common.id") || "ID"}</th>
              <th>{t("common.username") || "Username"}</th>
              <th>{t("common.email") || "Email"}</th>
              <th>{t("common.admin") || "Admin"}</th>
              <th>{t("common.disabled") || "Disabled"}</th>
              <th>{t("common.mustChange") || "MustChange"}</th>
              <th>{t("common.actions") || "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.id}</td>
                <td>{u.username}</td>
                <td>{u.email}</td>
                <td>{u.is_admin ? t("common.yes") : t("common.no")}</td>
                <td>{u.is_disabled ? t("common.yes") : t("common.no")}</td>
                <td>{u.must_change_password ? t("common.yes") : t("common.no")}</td>
                <td>
                  {u.is_disabled ? (
                    <Button
                      size="sm"
                      onClick={() => enableUser(u.id)}
                      className="me-1"
                    >
                      {t("admin.enable")}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="warning"
                      onClick={() => disableUser(u.id)}
                      className="me-1"
                    >
                      {t("admin.disable")}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="info"
                    onClick={() => viewUserTrackers(u)}
                    className="me-1"
                  >
                    <i className="bi bi-eye"></i> {t("admin.viewTrackers")}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => deleteUser(u.id)}
                    className="me-1"
                  >
                    {t("admin.delete")}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => resetPassword(u.id)}
                  >
                    {t("admin.resetPassword")}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Container>

      {selectedTracker && (
        <PriceChartModal
          show={showPriceChart}
          onHide={() => setShowPriceChart(false)}
          tracker={selectedTracker}
        />
      )}

      {selectedTracker && selectedTracker.flight_details && (
        <FlightSelectionModal
          show={showFlightSelection}
          onHide={() => setShowFlightSelection(false)}
          trackerId={selectedTracker.id}
          flightType={editingFlightType}
          currentFlight={
            editingFlightType === "outbound"
              ? selectedTracker.flight_details.outbound
              : selectedTracker.flight_details.return || undefined
          }
          onFlightSelected={handleFlightSelected}
          trackerDepartureDate={selectedTracker.departure_date}
          trackerReturnDate={selectedTracker.return_date || undefined}
          customGetAvailableFlights={handleGetAvailableFlights}
          customSelectFlight={handleSelectFlight}
        />
      )}
      
      <ConfirmDialog
        show={showDeleteConfirm}
        title={t("common.confirmDeleteUserTitle")}
        message={t("common.confirmDeleteUserMessage")}
        confirmText={t("common.confirmText")}
        cancelText={t("common.cancelText")}
        confirmVariant="danger"
        onConfirm={confirmDeleteUser}
        onCancel={() => setShowDeleteConfirm(false)}
      />
      
      <ConfirmDialog
        show={showResetPasswordConfirm}
        title={t("common.confirmResetPasswordTitle")}
        message={t("common.confirmResetPasswordMessage")}
        confirmText={t("common.confirmResetText")}
        cancelText={t("common.cancelText")}
        confirmVariant="warning"
        onConfirm={confirmResetPassword}
        onCancel={() => setShowResetPasswordConfirm(false)}
      />
      <Footer />
    </>
  );
};

export default Admin;
